import type { createClient } from "@/lib/supabase/client";
import type { Database } from "@/types/database";

type BrowserClient = ReturnType<typeof createClient>;

export type BrowserDocRow = Omit<
  Database["public"]["Tables"]["docs"]["Row"],
  "search_doc" | "updated_by" | "made_public_at" | "made_public_by" | "doc_number"
>;

export const DOC_INVENTORY_PAGE_SIZE = 500;

const DOC_INVENTORY_COLUMNS =
  "id,identifier,workspace_id,project_id,team_id,parent_id,position,title,content,icon,author_id,tags,pinned,created_at,updated_at,deleted_at,is_public";

/**
 * Load the complete live-doc inventory without assuming the PostgREST row cap.
 *
 * A hosted project can cap a requested 500-row range at (for example) 100
 * rows. Advance by the number of rows actually returned until the exact count
 * is satisfied (with an empty page as a defensive fallback); stopping merely
 * because a page is shorter than the requested range would silently truncate
 * the workspace at that server-side cap.
 */
export async function loadAllLiveDocs(
  client: BrowserClient,
  workspaceId?: string
): Promise<BrowserDocRow[]> {
  const rows: BrowserDocRow[] = [];
  let from = 0;

  for (;;) {
    let query = client
      .from("docs")
      .select(DOC_INVENTORY_COLUMNS, { count: "exact" })
      .is("deleted_at", null)
      .order("updated_at", { ascending: false })
      .order("id", { ascending: false });

    if (workspaceId) query = query.eq("workspace_id", workspaceId);

    const { data, error, count } = await query.range(
      from,
      from + DOC_INVENTORY_PAGE_SIZE - 1
    );
    if (error) throw error;

    const page = data ?? [];
    if (page.length === 0) return rows;

    rows.push(...page);
    if (count !== null && rows.length >= count) return rows;
    from += page.length;
  }
}
